import React from 'react'
import { ScaleLinear } from 'd3-scale'
import { config } from './config'
import { Line, Area } from 'd3-shape'

export const HorizontalTicks = ({
  values,
  y,
  className,
  x
}: {
  values: EasyChartArrayType
  y: ScaleLinear<number, number>
  x: [number, number]
  className: string
}) => {
  return (
    <g className={className || 'ticks'}>
      {values.map((tick, i) => (
        <line
        key={i}
          x1={x[0]}
          x2={x[1]}
          y1={y(tick[0] || tick)}
          y2={y(tick[0] || tick)}
          className={
            i === 0 ? 'first' : i === values.length - 1 ? 'last' : undefined
          }
        />
      ))}
      {values.slice(0,values.length-1).map((tick, i) => (
        <text
        key={i}
          x={x[0]}
          y={y(tick)-8}
          textAnchor='start'
          alignmentBaseline='middle'
          className={
            i === 0 ? 'first' : i === values.length - 1 ? 'last' : undefined
          }
        >
          {tick[1] || tick}
        </text>
      ))}
    </g>
  )
}
export const VerticalTicks = ({
  values,
  y,
  className,
  x
}: {
  values: EasyChartArrayType
  x: ScaleLinear<number, number>
  y: [number, number]
  className: string
}) => {
  return (
    <g className={className || 'ticks'}>
      {values.map((tick,i) => (
        <line
        key={i}
          y1={y[0]}
          y2={y[1]}
          x1={x(tick[0] || tick)}
          x2={x(tick[0] || tick)}
        />
      ))}
      {values.map((tick,i) => (
        <text
        key={i}
          y={y[1] + 15}
          x={x(tick[0] || tick)}
          textAnchor='middle'
          alignmentBaseline='middle'
        >
          {tick[1] || tick}
        </text>
      ))}
    </g>
  )
}
export const Annotations = ({
  values,
  x,
  y
}: {
  values: EasyChartArrayType
  x: ScaleLinear<number, number>
  y: ScaleLinear<number, number>
}) => {
  return (
    <g className='annotations'>
      {values.map((anno,i) => {
        let linespace = -config.linespace
        let offset = anno.offset
        if (!offset) offset = [0, 0]
        if (offset[0] < 0) linespace = config.linespace

        return (
          <g key={i} className={anno.className ? anno.className : undefined}>
            <text y={y(anno.pos[1]) + offset[1]}>
              {anno.text.split('##').map((line, i) => {
                return (
                  <tspan
                  key={i}
                    dy={
                      i > 0
                        ? config.annoLineHeight
                        : anno.dir === 'vertical'
                        ? offset[1] > 0
                          ? config.annoLineHeight
                          : 0
                        : 0
                    }
                    x={x(anno.pos[0]) + offset[0]}
                    textAnchor={anno.align || undefined}
                  >
                    {line}
                  </tspan>
                )
              })}
            </text>
            {anno.dir === 'horizontal' && anno.offset && (
              <line
                x1={x(anno.pos[0]) + -linespace}
                x2={x(anno.pos[0]) + offset[0] + linespace}
                y1={y(anno.pos[1]) + offset[1]}
                y2={y(anno.pos[1]) + offset[1]}
              />
            )}
            {anno.dir === 'vertical' && anno.offset && (
              <line
                x1={x(anno.pos[0]) + offset[0]}
                x2={x(anno.pos[0]) + offset[0]}
                y1={y(anno.pos[1]) + linespace}
                y2={y(anno.pos[1]) + offset[1] + -linespace}
              />
            )}
          </g>
        )
      })}
    </g>
  )
}
export const Curve = ({
  values,
  className,
  clipped,
  line,
  area
}: {
  values: EasyChartArrayType
  clipped?: EasyChartArrayType
  className: string
  line: Line<EasyChartArrayType>
  area: Area<EasyChartArrayType>
}) => {
  let id =
    '_' +
    Math.random()
      .toString(36)
      .substr(2, 9)

  return (
    <g>
      {clipped && (
        <clipPath id={id}>
          <path d={area(clipped)} />
        </clipPath>
      )}
      <path
        d={line(values)}
        className={className ? className : 'curve'}
        clipPath={clipped ? 'url(#' + id + ')' : undefined}
      />
    </g>
  )
}

export const FilledArea = ({
  values,
  className,
  clipped,
  area
}: {
  values: EasyChartArrayType
  clipped?: EasyChartArrayType
  className: string
  area: Area<EasyChartArrayType>
}) => {
  let id =
    '_' +
    Math.random()
      .toString(36)
      .substr(2, 9)

  return (
    <g>
      {clipped && (
        <clipPath id={id}>
          <path d={area(clipped)} />
        </clipPath>
      )}
      <path
        d={area(values)}
        className={className ? className : 'area'}
        clipPath={clipped ? 'url(#' + id + ')' : undefined}
      />
    </g>
  )
}

